import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { FONT_FAMILY_OPTIONS, IMAGE_FIELDS, isShapeId } from "./constants";

// ─── small inline helper components ─────────────────────────────────────
function NumberField({ label, value, onChange, onCommit, step = 0.5, min, testId }) {
  return (
    <div>
      <Label className="overline block mb-1">{label}</Label>
      <Input
        type="number" step={step} min={min}
        value={value ?? ""}
        onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
        onBlur={onCommit}
        className="rounded-xl h-9 font-mono text-xs"
        data-testid={testId}
      />
    </div>
  );
}

function ColorField({ label, value, fallback, onChange, onCommit, placeholder, testId }) {
  const hex = value?.startsWith("#") ? value : fallback;
  return (
    <div>
      <Label className="overline block mb-1">{label}</Label>
      <div className="flex gap-1">
        <input
          type="color" value={hex}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onCommit}
          className="h-9 w-9 border border-border cursor-pointer" data-testid={testId}
        />
        <Input
          value={value || ""} placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onCommit}
          className="rounded-xl h-9 text-xs font-mono flex-1"
        />
      </div>
    </div>
  );
}

function ToggleRow({ label, checked, onChange, testId }) {
  return (
    <div className="flex items-center gap-2">
      <Switch checked={!!checked} onCheckedChange={onChange} data-testid={testId} />
      <Label className="text-xs">{label}</Label>
    </div>
  );
}

// ─── main panel ─────────────────────────────────────────────────────────
/**
 * "Properti Field" side card — edits the currently-selected layout field.
 * Renders 3 mutually-exclusive branches based on the field kind:
 *   1. IMAGE fields → width + height only
 *   2. Shape fields (rectangle/line) → dimensions, stroke, fill, radius
 *   3. Text fields → font size, weight, alignment, family, color, static text
 *
 * Extracted from `LayoutEditor.jsx` Feb 2026 to reduce parent from 613 → ~380
 * lines. All state remains in the parent; this component is fully controlled
 * via props.
 */
export default function PropertiesPanel({
  selectedId, selected, layout, defaults,
  updateField, commitChange, setLayout, history,
  onDeleteField,
}) {
  if (!selected) {
    return (
      <div className="border border-border bg-card p-4">
        <div className="overline mb-3">Properti Field</div>
        <div className="text-sm text-muted-foreground">
          Klik field pada preview untuk edit properti.
        </div>
      </div>
    );
  }

  // Helper — apply a partial update + commit history in same tick.
  const setAndCommit = (patch) => {
    updateField(selectedId, patch);
    setTimeout(commitChange, 0);
  };

  const isImg = IMAGE_FIELDS.has(selectedId);
  const isShape = isShapeId(selectedId);

  const onResetField = () => {
    const d = defaults[selectedId];
    if (!d) return;
    const next = { ...layout, [selectedId]: { ...d } };
    setLayout(next);
    history.commit(next);
    toast.success("Field direset ke default");
  };

  const isCustom = selectedId?.startsWith("custom_") || selectedId?.startsWith("shape_");

  return (
    <div className="border border-border bg-card p-4">
      <div className="overline mb-3">Properti Field</div>
      <div className="space-y-3">
        <div>
          <div className="overline">Field</div>
          <div className="font-mono font-bold text-sm">{selectedId}</div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <NumberField label="X (pt)" value={selected.x}
            onChange={(v) => updateField(selectedId, { x: v })}
            onCommit={commitChange} testId="prop-x" />
          <NumberField label="Y (pt)" value={selected.y}
            onChange={(v) => updateField(selectedId, { y: v })}
            onCommit={commitChange} testId="prop-y" />
        </div>

        {isImg && (
          <div className="grid grid-cols-2 gap-2">
            <NumberField label="Lebar (pt)" value={selected.w || 60}
              onChange={(v) => updateField(selectedId, { w: v })}
              onCommit={commitChange} step={1} testId="prop-w" />
            <NumberField label="Tinggi (pt)" value={selected.h || 60}
              onChange={(v) => updateField(selectedId, { h: v })}
              onCommit={commitChange} step={1} testId="prop-h" />
          </div>
        )}

        {isShape && (
          <div className="space-y-3" data-testid="shape-props">
            <div>
              <Label className="overline block mb-1">Jenis</Label>
              <Select value={selected.type || "rectangle"}
                onValueChange={(v) => setAndCommit({ type: v })}>
                <SelectTrigger className="rounded-xl h-9 text-xs" data-testid="prop-shape-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="rectangle">Kotak (Rectangle)</SelectItem>
                  <SelectItem value="line">Garis (Line)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <NumberField
                label={selected.type === "line" ? "Panjang X (pt)" : "Lebar (pt)"}
                value={selected.w ?? 100}
                onChange={(v) => updateField(selectedId, { w: v })}
                onCommit={commitChange} testId="prop-shape-w" />
              <NumberField
                label={selected.type === "line" ? "Panjang Y (pt)" : "Tinggi (pt)"}
                value={selected.h ?? 60}
                onChange={(v) => updateField(selectedId, { h: v })}
                onCommit={commitChange} testId="prop-shape-h" />
            </div>
            <ColorField label="Warna Garis" value={selected.stroke_color} fallback="#111111"
              onChange={(v) => updateField(selectedId, { stroke_color: v })}
              onCommit={commitChange} placeholder="#hex / green / orange"
              testId="prop-shape-stroke-color" />
            <NumberField label="Ketebalan Garis (pt)" value={selected.stroke_width ?? 0.8}
              step={0.1} min={0.1}
              onChange={(v) => updateField(selectedId, { stroke_width: v || 0.8 })}
              onCommit={commitChange} testId="prop-shape-stroke-width" />
            {selected.type !== "line" && (
              <>
                <div>
                  <ColorField label="Warna Isi (opsional)" value={selected.fill_color} fallback="#ffffff"
                    onChange={(v) => updateField(selectedId, { fill_color: v })}
                    onCommit={commitChange} placeholder="none / #hex"
                    testId="prop-shape-fill-color" />
                  <p className="text-[10px] text-muted-foreground mt-1">
                    Ketik <code>none</code> untuk tanpa isi (border saja).
                  </p>
                </div>
                <NumberField label="Radius Sudut (pt)" value={selected.radius ?? 0} min={0}
                  onChange={(v) => updateField(selectedId, { radius: v })}
                  onCommit={commitChange} testId="prop-shape-radius" />
              </>
            )}
          </div>
        )}

        {!isImg && !isShape && (
          <>
            <NumberField label="Ukuran Font" value={selected.size || 8}
              onChange={(v) => updateField(selectedId, { size: v || 8 })}
              onCommit={commitChange} testId="prop-size" />
            <div className="flex items-center justify-between border-t border-border pt-2">
              <Label className="text-sm">Bold</Label>
              <Switch checked={!!selected.bold}
                onCheckedChange={(v) => setAndCommit({ bold: v })}
                data-testid="prop-bold" />
            </div>
            <div>
              <Label className="overline block mb-1">Align</Label>
              <Select value={selected.align || "left"}
                onValueChange={(v) => setAndCommit({ align: v })}>
                <SelectTrigger className="rounded-xl h-9 text-xs" data-testid="prop-align"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="left">Left</SelectItem>
                  <SelectItem value="center">Center</SelectItem>
                  <SelectItem value="right">Right</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="overline block mb-1">Font</Label>
                <Select value={selected.font || "helvetica"}
                  onValueChange={(v) => setAndCommit({ font: v })}>
                  <SelectTrigger className="rounded-xl h-9 text-xs" data-testid="prop-font"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {FONT_FAMILY_OPTIONS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <ColorField label="Warna" value={selected.color} fallback="#111111"
                onChange={(v) => updateField(selectedId, { color: v })}
                onCommit={commitChange} placeholder="black / #hex" testId="prop-color" />
            </div>
            <div className="grid grid-cols-2 gap-2 border-t border-border pt-2">
              <ToggleRow label="Italic" checked={selected.italic}
                onChange={(v) => setAndCommit({ italic: v })} testId="prop-italic" />
              <ToggleRow label="Underline" checked={selected.underline}
                onChange={(v) => setAndCommit({ underline: v })} testId="prop-underline" />
            </div>
            {selected.text !== undefined && (
              <div>
                <Label className="overline block mb-1">Teks Statis</Label>
                <Input value={selected.text || ""}
                  onChange={(e) => updateField(selectedId, { text: e.target.value })}
                  onBlur={commitChange}
                  className="rounded-xl h-9 text-xs" data-testid="prop-text" />
              </div>
            )}
          </>
        )}

        <div className="flex items-center justify-between border-t border-border pt-2">
          <Label className="text-sm">Sembunyikan</Label>
          <Switch checked={!!selected.hidden}
            onCheckedChange={(v) => setAndCommit({ hidden: v })}
            data-testid="prop-hidden" />
        </div>

        {isCustom ? (
          <Button variant="outline" className="rounded-xl w-full text-destructive"
                  onClick={() => onDeleteField(selectedId)} data-testid="delete-field-button">
            <Trash size={14} className="mr-2" />
            {selectedId?.startsWith("shape_") ? "Hapus Shape" : "Hapus Field Custom"}
          </Button>
        ) : (
          <Button variant="outline" className="rounded-xl w-full"
                  onClick={onResetField} data-testid="reset-field-button">
            Reset Field Ini
          </Button>
        )}
      </div>
    </div>
  );
}
